/**
 * AudioManager - Singleton for managing meeting audio sounds
 * 
 * This module solves several issues:
 * 1. Multiple Audio object creations causing duplicate network requests
 * 2. Range requests (206 partial content) causing multiple fetches
 * 3. Audio not being preloaded/cached properly
 * 4. Blocking during audio load
 * 
 * Usage:
 *   import { audioManager } from 'src/consumer/utils/audioManager'
 *   
 *   // Preload during idle time (call early, e.g., when component mounts)
 *   audioManager.preload()
 *   
 *   // Play sounds
 *   audioManager.playJoinSound()
 *   audioManager.playLeaveSound()
 */

// Audio file paths
const AUDIO_FILES = {
  join: '/meeting_join.mp3',
  leave: '/meeting_leave.mp3',
}

// Singleton state
let audioCache = {
  join: null,
  leave: null,
}
let preloadPromise = null
let isPreloaded = false

/**
 * Preload audio files into memory using fetch + blob
 * This avoids the Range request issue by loading the entire file at once
 * and creating Audio from a blob URL (fully cached in memory)
 */
async function preloadAudioFile(url) {
  try {
    const response = await fetch(url, {
      // Disable range requests by not accepting partial content
      headers: {
        'Range': '', // Clear any range header
      },
      // Use cache for subsequent requests
      cache: 'force-cache',
    })
    
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}: ${response.status}`)
    }
    
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    
    const audio = new Audio(blobUrl)
    audio.preload = 'auto'
    
    // Wait for audio to be fully loaded
    await new Promise((resolve, reject) => {
      audio.addEventListener('canplaythrough', resolve, { once: true })
      audio.addEventListener('error', reject, { once: true })
      audio.load()
    })
    
    return audio
  } catch (error) {
    console.warn(`[AudioManager] Failed to preload ${url}:`, error.message)
    // Fallback to regular Audio object
    return new Audio(url)
  }
}

/**
 * Preload all audio files
 * Safe to call multiple times - only preloads once
 * Uses requestIdleCallback for non-blocking load
 */
function preload() {
  if (isPreloaded || preloadPromise) {
    return preloadPromise || Promise.resolve()
  }
  
  preloadPromise = new Promise((resolve) => {
    const doPreload = async () => {
      try {
        const [joinAudio, leaveAudio] = await Promise.all([
          preloadAudioFile(AUDIO_FILES.join),
          preloadAudioFile(AUDIO_FILES.leave),
        ])
        
        audioCache.join = joinAudio
        audioCache.leave = leaveAudio
        isPreloaded = true
        
        console.log('[AudioManager] Audio files preloaded successfully')
      } catch (error) {
        console.warn('[AudioManager] Preload failed, will use fallback:', error.message)
      }
      resolve()
    }
    
    // Use requestIdleCallback if available for non-blocking load
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(doPreload, { timeout: 3000 })
    } else {
      // Fallback: use setTimeout to defer
      setTimeout(doPreload, 100)
    }
  })
  
  return preloadPromise
}

/**
 * Play join sound
 * Non-blocking - fires and forgets
 */
function playJoinSound() {
  try {
    if (audioCache.join) {
      // Clone the audio to allow overlapping plays
      audioCache.join.currentTime = 0
      audioCache.join.play().catch(() => {
        // Ignore play errors (e.g., user interaction required)
      })
    } else {
      // Fallback: create temporary audio
      const audio = new Audio(AUDIO_FILES.join)
      audio.play().catch(() => {})
    }
  } catch (error) {
    // Silently fail
  }
}

/**
 * Play leave sound
 * Non-blocking - fires and forgets
 */
function playLeaveSound() {
  try {
    if (audioCache.leave) {
      audioCache.leave.currentTime = 0
      audioCache.leave.play().catch(() => {
        // Ignore play errors
      })
    } else {
      // Fallback
      const audio = new Audio(AUDIO_FILES.leave)
      audio.play().catch(() => {})
    }
  } catch (error) {
    // Silently fail
  }
}

/**
 * Check if audio is preloaded
 */
function isReady() {
  return isPreloaded
}

/**
 * Cleanup - call on app unmount if needed
 */
function cleanup() {
  if (audioCache.join) {
    const url = audioCache.join.src
    audioCache.join = null
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url)
    }
  }
  if (audioCache.leave) {
    const url = audioCache.leave.src
    audioCache.leave = null
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url)
    }
  }
  isPreloaded = false
  preloadPromise = null
}

// Export singleton
export const audioManager = {
  preload,
  playJoinSound,
  playLeaveSound,
  isReady,
  cleanup,
}

// Also export individual functions for convenience
export { preload, playJoinSound, playLeaveSound, isReady, cleanup }

export default audioManager

